interface ThresholdRanges {
  danger: number
  warning: number
}

interface ThresholdLabels {
  danger: string
  warning: string
  normal: string
}

type Severity = "danger" | "warning" | "normal"

const severityStyles: Record<
  Severity,
  { title: string; value: string; label: string; container: string }
> = {
  danger: {
    title: "text-coval-text-muted",
    label: "text-coval-danger-on-surface",
    value: "text-coval-danger-on-surface",
    container: "border-coval-danger-border bg-coval-danger-surface",
  },
  warning: {
    title: "text-coval-text-muted",
    label: "text-coval-warning-on-surface",
    value: "text-coval-warning-on-surface",
    container: "border-coval-warning-border bg-coval-warning-surface",
  },
  normal: {
    title: "text-coval-text-muted",
    label: "text-coval-text-muted",
    value: "text-foreground",
    container: "border-divider bg-content1",
  },
}

const defaultStyles = {
  title: "text-coval-text-muted",
  label: "text-coval-text-muted",
  value: "text-foreground",
  container: "border-divider bg-content1",
} as const

function getSeverity(value: number, ranges: ThresholdRanges): Severity {
  if (value > ranges.danger) {
    return "danger"
  }
  if (value > ranges.warning) {
    return "warning"
  }
  return "normal"
}

interface ThresholdMetricBoxProps {
  title: string
  value: number
  ranges?: ThresholdRanges
  labels?: ThresholdLabels
  formatValue?: (value: number) => string
  unit?: string
}

export function ThresholdMetricBox({
  title,
  value,
  ranges,
  labels,
  formatValue = (v) => v.toFixed(2),
  unit,
}: ThresholdMetricBoxProps) {
  const styles = ranges
    ? severityStyles[getSeverity(value, ranges)]
    : defaultStyles
  const label =
    ranges && labels ? labels[getSeverity(value, ranges)] : undefined
  return (
    <div className={`p-3 rounded-lg border ${styles.container}`}>
      <div className="flex items-baseline justify-between mb-1">
        <span className={`text-sm font-medium ${styles.title}`}>{title}</span>
        {label && (
          <span className={`text-xs font-normal ${styles.label}`}>{label}</span>
        )}
      </div>
      <div className={`text-2xl font-bold ${styles.value}`}>
        {formatValue(value)}
        {unit && <span className="text-sm font-normal ml-1">{unit}</span>}
      </div>
    </div>
  )
}
